import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ClosedIcon from '@mui/icons-material/DoDisturbRounded';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import NotInterestedRoundedIcon from '@mui/icons-material/NotInterestedRounded';
import SyncDisabledRoundedIcon from '@mui/icons-material/SyncDisabledRounded';
import Chip, { ChipVariantEnum } from 'components/Chips/Chip';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import QButton from 'components/QButton';
import QTip from 'components/QuickenControls/QTip';

import PropTypes from 'prop-types';
import React from 'react';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  statusChip: {
    marginRight: 10,
  },
  a2aButton: {
    marginRight: 10,
    marginTop: 'auto',
    marginBottom: 'auto',
    backgroundColor: theme.palette.background.default,
  },
  divider: {
    marginLeft: 10,
    marginRight: 10,
    marginTop: 'auto',
    marginBottom: 'auto',
    height: 50,
    borderLeft: `solid 1px ${theme.palette.border.main}`,
  },
  rightRowContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoIcon: {
    marginLeft: -7,
    marginTop: -1,
  },
}));

const AccountStatusChipList = ({
  accountStatus,
  kickoffMicrodepositFlow = undefined,
  //  showFailureDialog,
  hideChips = undefined,
  tooltipText = undefined,
  className = undefined,
}) => {
  const { classes, cx } = useStyles();

  const showErrorChip = accountStatus?.error?.showChip && !hideChips?.error;
  const showDisconnectedChip = accountStatus?.disconnected?.showChip && !hideChips?.disconnected;
  const showPendingChip =
    (accountStatus?.depositPending?.showChip || accountStatus?.depositPending?.isPending) && !hideChips?.depositPending;

  return (
    <div className={cx(classes.rightRowContainer, className)}>
      {
        //  Ineligible chip should ONLY appear alone when no other reasons for an account being ineligible are given
        accountStatus?.ineligible?.showChip &&
          !accountStatus?.disconnected?.showChip &&
          !accountStatus?.closed?.showChip &&
          !accountStatus?.verified?.showChip &&
          !accountStatus?.depositPending?.showChip &&
          !accountStatus?.depositPending?.isPending &&
          !accountStatus?.error?.showChip && (
            <>
              <Chip chipStyle={classes.statusChip} variant={ChipVariantEnum.INACTIVE}>
                Not eligible
              </Chip>
              <InfoTooltipIcon
                size='medium'
                className={classes.infoIcon}
                tooltipPlacement='top'
                tooltipText={
                  accountStatus?.fundingAccountFailure?.showChip
                    ? accountStatus?.fundingAccountFailure?.tooltipText
                    : accountStatus?.ineligible?.tooltipText
                }
              />
            </>
          )
      }
      {accountStatus?.business?.showChip && (
        <Chip chipStyle={classes.statusChip} variant={ChipVariantEnum.BUSINESS}>
          Business
        </Chip>
      )}
      {accountStatus?.verified?.showChip && !hideChips?.verified && (
        <Chip chipStyle={classes.statusChip} icon={CheckCircleIcon} variant={ChipVariantEnum.SUCCESS}>
          Transfers enabled
        </Chip>
      )}
      {showPendingChip && (
        <Chip chipStyle={classes.statusChip} icon={MonetizationOnIcon} variant={ChipVariantEnum.PENDING}>
          {accountStatus?.depositPending?.manualVerification ? 'Verification deposits pending' : 'Pending verification'}
        </Chip>
      )}
      {/* accountStatus?.fundingAccountFailure?.showChip && !hideChips?.fundingAccountFailure && (
        <Chip chipStyle={classes.statusChip} icon={WarningRoundedIcon} variant={ChipVariantEnum.WARNING}>
          Transfer verification failed
        </Chip>
      ) */}
      {accountStatus?.depositPending?.showChip &&
        kickoffMicrodepositFlow &&
        !hideChips?.depositPending &&
        accountStatus?.depositPending?.manualVerification && (
          <>
            {tooltipText && !accountStatus?.depositPending?.enableTransferFunctions ? (
              <QTip placement='top' solidTheme title={tooltipText} noTippyTip arrow enableOnMobile>
                <span style={{ display: 'inline-block' }}>
                  <QButton onClick={kickoffMicrodepositFlow} variant='outlined' className={classes.a2aButton} disabled>
                    {'Verify'}
                  </QButton>
                </span>
              </QTip>
            ) : (
              <QButton
                onClick={kickoffMicrodepositFlow}
                variant='outlined'
                className={classes.a2aButton}
                disabled={!accountStatus?.depositPending?.enableTransferFunctions}
              >
                {'Verify'}
              </QButton>
            )}
          </>
        )}
      {/* accountStatus?.fundingAccountFailure?.showChip &&
        showFailureDialog &&
        !hideChips?.fundingAccountFailure && (
          <>
            {tooltipText ? (
              <QTip placement='top' solidTheme title={tooltipText} noTippyTip arrow enableOnMobile>
                <span style={{ display: 'inline-block' }}>
                  <QButton onClick={showFailureDialog} variant='outlined' className={classes.a2aButtonDisabled}>
                    {'View'}
                  </QButton>
                </span>
              </QTip>
            ) : (
              <QButton onClick={showFailureDialog} variant='outlined' className={classes.a2aButton}>
                {'View'}
              </QButton>
            )}
          </>
        ) */}
      {(showErrorChip || showDisconnectedChip) &&
        showPendingChip &&
        accountStatus?.depositPending?.manualVerification &&
        kickoffMicrodepositFlow && <div className={classes.divider} />}
      {showDisconnectedChip && (
        <Chip chipStyle={classes.statusChip} icon={SyncDisabledRoundedIcon}>
          Account disconnected
        </Chip>
      )}
      {showErrorChip && (
        <Chip chipStyle={cx(classes.statusChip, 'error')} icon={ErrorIcon}>
          Account error
        </Chip>
      )}
      {accountStatus?.closed?.showChip && !hideChips?.closed && (
        <Chip chipStyle={classes.statusChip} icon={NotInterestedRoundedIcon}>
          Account closed
        </Chip>
      )}
      {
        //  Same as status closed except we show different text!
        accountStatus?.closedInTxn?.showChip && !hideChips?.closedInTxn && (
          <Chip icon={ClosedIcon} chipStyle={classes.statusChip}>
            Closed
          </Chip>
        )
      }
    </div>
  );
};

AccountStatusChipList.propTypes = {
  accountStatus: PropTypes.object,
  //  showFailureDialog: PropTypes.func,
  kickoffMicrodepositFlow: PropTypes.func,
  hideChips: PropTypes.object,
  tooltipText: PropTypes.string,
  className: PropTypes.string,
};

export default AccountStatusChipList;
